/**
 * Bot types a game offers. Everything here is generic: a package publishes descriptors
 * (`Variant.bots`) and the client renders and validates them without knowing any game.
 */
export interface BotChoiceOption {
    value: string;
    label: string;
}
export interface BotParamSpec {
    id: string;
    label: string;
    help?: string;
    kind: "integer" | "boolean" | "choice";
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    options?: BotChoiceOption[];
    default: number | boolean | string;
}
export interface BotTypeDescriptor {
    id: string;
    version: string;
    label: string;
    summary: string;
    params: BotParamSpec[];
}
/** A seat's public bot choice (the host never exposes the seed). */
export interface BotProfile {
    type: string;
    version: string;
    label: string;
    params: Record<string, number | boolean | string>;
}
export type BotParams = Record<string, number | boolean | string>;
/** The request body for a non-default bot, as accepted by the lobby API. */
export interface BotRequest {
    type: string;
    version: string;
    params: BotParams;
}
/** What the setup dialog remembers per game. */
export interface BotChoice {
    type: string;
    version: string;
    params: BotParams;
}

export const DEFAULT_BOT = "default";

/** Types a variant can seat (default first); empty when the game offers only the house bot. */
export function selectableBots(bots: BotTypeDescriptor[] | undefined | null): BotTypeDescriptor[] {
    if (!bots || bots.length < 2) return [];
    return [...bots].sort((a, b) => Number(b.id === DEFAULT_BOT) - Number(a.id === DEFAULT_BOT));
}

export function defaultParams(bot: BotTypeDescriptor): BotParams {
    return Object.fromEntries(bot.params.map((p) => [p.id, p.default]));
}

/** True when `value` is an acceptable value for `spec`. Integers must be whole numbers, on-step and in range. */
export function validParam(spec: BotParamSpec, value: unknown): boolean {
    switch (spec.kind) {
        case "integer": {
            if (typeof value !== "number" || !Number.isInteger(value)) return false;
            const min = spec.min ?? Number.MIN_SAFE_INTEGER;
            const max = spec.max ?? Number.MAX_SAFE_INTEGER;
            if (value < min || value > max) return false;
            return !spec.step || spec.step < 1 || (value - min) % spec.step === 0;
        }
        case "boolean":
            return typeof value === "boolean";
        case "choice":
            return typeof value === "string" && !!spec.options?.some((o) => o.value === value);
    }
}

/**
 * Resolves remembered or edited values against the descriptor: every declared parameter is present,
 * unknown keys are dropped, and anything invalid falls back to its default. `changed` reports whether
 * anything had to be replaced, so the caller can tell the player.
 */
export function resolveBotParams(bot: BotTypeDescriptor, supplied: unknown): { params: BotParams; changed: boolean } {
    const given = supplied && typeof supplied === "object" && !Array.isArray(supplied) ? (supplied as Record<string, unknown>) : {};
    let changed = Object.keys(given).some((key) => !bot.params.some((p) => p.id === key));
    const params: BotParams = {};
    for (const spec of bot.params) {
        if (!(spec.id in given)) {
            params[spec.id] = spec.default;
            continue;
        }
        if (validParam(spec, given[spec.id])) params[spec.id] = given[spec.id] as number | boolean | string;
        else {
            params[spec.id] = spec.default;
            changed = true;
        }
    }
    return { params, changed };
}

/**
 * Sets one parameter and keeps every other value exactly as typed: another field that is currently invalid stays
 * invalid (and visible as such) instead of being silently reset to its default.
 */
export function setBotParam(bot: BotTypeDescriptor, params: BotParams, id: string, value: number | boolean | string): BotParams {
    return { ...defaultParams(bot), ...params, [id]: value };
}

export function sameParams(bot: BotTypeDescriptor, a: BotParams, b: BotParams): boolean {
    return bot.params.every((p) => a[p.id] === b[p.id]);
}

/** The API body for a choice; undefined for the default bot, which keeps the historical `bot: true` request. */
export function botRequest(types: BotTypeDescriptor[], choice: BotChoice | null | undefined): BotRequest | undefined {
    if (!choice || choice.type === DEFAULT_BOT) return undefined;
    const bot = types.find((t) => t.id === choice.type);
    if (!bot) return undefined;
    return { type: bot.id, version: bot.version, params: resolveBotParams(bot, choice.params).params };
}

/** A remembered choice survives only while the game still offers that exact type and version. */
export function restoreBotChoice(types: BotTypeDescriptor[], saved: unknown): { choice: BotChoice; changed: boolean } | null {
    if (!saved || typeof saved !== "object") return null;
    const { type, version, params } = saved as Partial<BotChoice>;
    const bot = types.find((t) => t.id === type && t.version === version);
    if (!bot || bot.id === DEFAULT_BOT) return null;
    const resolved = resolveBotParams(bot, params);
    return { choice: { type: bot.id, version: bot.version, params: resolved.params }, changed: resolved.changed };
}

/** "Aggression 30%", "Play style: Seafarer": one entry per parameter, for tooltips and summaries. */
export function describeBotParams(bot: Pick<BotTypeDescriptor, "params"> | undefined, params: Record<string, unknown>): string[] {
    if (!bot) return Object.entries(params).map(([key, value]) => `${key} ${String(value)}`);
    return bot.params.flatMap((spec) => {
        const value = params[spec.id];
        if (value === undefined) return [];
        if (spec.kind === "integer") return [`${spec.label} ${value}${spec.unit ?? ""}`];
        if (spec.kind === "boolean") return [`${spec.label}: ${value ? "on" : "off"}`];
        return [`${spec.label}: ${spec.options?.find((o) => o.value === value)?.label ?? String(value)}`];
    });
}

/** The line under a bot's name: "Bot · Advanced" for a typed bot, the historical "Bot · ready" otherwise. */
export function botSubtitle(profile: Pick<BotProfile, "label"> | null | undefined): string {
    return profile ? `Bot · ${profile.label}` : "Bot · ready";
}
